<template>
  <v-card class="pa-6">
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-overlay>
    <v-card-title class="text-uppercase">
      <v-row class="my-1 justify-space-between align-center">
        <div>{{ this.company === null ? 'Crear ' : 'Detalle' }} Empresa</div>
      </v-row>
    </v-card-title>
    <hr class="my-3" />
    <v-card-text class="my-0 py-0">
      <v-form ref="newCompanyForm" v-model="formValid">
        <v-row dense>
          <v-col cols="12">
            <v-text-field
              v-model="newCompany.name"
              label="Nombre *"
              :rules="nameRules"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row dense class="align-center">
          <v-col cols="12" md="6">
            <v-select
              v-model="countrySelected"
              label="País"
              :items="countries"
              @change="changeCountry"
              item-value="id"
              item-text="name"
              outlined
              dense
              hide-details="auto"
              append-icon="mdi-arrow-down"
            ></v-select>
          </v-col>
          <v-col v-if="company !== null" cols="12" md="6">
            <v-switch
              v-model="newCompany.active"
              :label="newCompany.active ? 'Activa' : 'Inactiva'"
              color="green"
              class="mt-0 pt-0"
              hide-details
              inset
            ></v-switch>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col v-for="field in imageFields" :key="field.key" cols="12" md="6">
            <div class="d-flex align-center">
              <v-file-input
                v-model="images[field.key].file"
                :label="field.label"
                accept="image/png,image/jpeg,.svg,image/svg+xml"
                :rules="imageRules"
                :placeholder="currentFileName(images[field.key].url)"
                :persistent-placeholder="!!images[field.key].url"
                prepend-icon=""
                prepend-inner-icon="mdi-image-outline"
                outlined
                dense
                hide-details="auto"
                class="image-input"
                @change="onImageChange(field.key)"
              ></v-file-input>
              <v-sheet
                outlined
                rounded
                :width="field.previewWidth"
                height="40"
                :style="[previewBoxStyle, imagePreviewSrc(field.key) && clickableStyle]"
                :title="imagePreviewSrc(field.key) ? 'Ver en grande' : ''"
                class="d-flex align-center justify-center flex-shrink-0 ml-2 pa-1"
                @click="openViewer(imagePreviewSrc(field.key), field.viewerTitle)"
              >
                <v-icon v-if="isPreviewBroken(field.key)" color="error" title="No se pudo cargar la imagen">
                  mdi-image-broken-variant
                </v-icon>
                <img
                  v-else-if="imagePreviewSrc(field.key)"
                  :src="imagePreviewSrc(field.key)"
                  :alt="field.label"
                  :style="previewImgStyle"
                  @error="markPreviewBroken(field.key)"
                />
                <v-icon v-else color="grey">mdi-image-off-outline</v-icon>
              </v-sheet>
              <v-btn
                icon
                small
                class="ml-1"
                :title="field.removeTitle"
                :disabled="!images[field.key].url && !images[field.key].file"
                @click="removeImage(field.key)"
              >
                <v-icon small>mdi-image-remove</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-alert v-if="saveError" type="error" dense outlined class="mt-4 mb-2">{{ saveError }}</v-alert>
    <hr class="my-3" />
    <v-card-actions class="justify-end">
      <v-btn text color="primary" rounded @click="closeDialog">Cerrar</v-btn>
      <v-btn
        color="primary"
        rounded
        :loading="loading"
        @click="validate"
      >
        Guardar
      </v-btn>
    </v-card-actions>
    <image-preview-dialog v-model="viewer.open" :src="viewer.src" :title="viewer.title" />
  </v-card>
</template>

<script>
import CompaniesService from '../services/companie.service';
import CountryService from '../services/countries.service';
import FileUtils, { MAX_IMAGE_BYTES } from '../utils/file.utils';
import { PREVIEW_BOX_STYLE, PREVIEW_IMG_STYLE } from '../utils/image-preview.styles';
import ImagePreviewDialog from './ImagePreviewDialog.vue';


// Logo e ícono de la app comparten UX; cada uno se envía con su propio par URL / base64.
const IMAGE_FIELDS = [
  { key: 'logo', label: 'Logo (PNG, JPG o SVG, máx. 1 MB)', removeTitle: 'Quitar logo', previewWidth: 120, viewerTitle: 'Logo' },
  { key: 'iconApp', label: 'Ícono app (PNG, JPG o SVG, máx. 1 MB)', removeTitle: 'Quitar ícono', previewWidth: 40, viewerTitle: 'Ícono app' }
];

const SAVE_ERRORS = {
  INVALID_IMAGE_FORMAT: 'La imagen debe ser PNG, JPG o SVG',
  IMAGE_TOO_LARGE: 'La imagen supera 1 MB',
  UNSAFE_SVG: 'El SVG contiene contenido no permitido'
};
const DEFAULT_SAVE_ERROR = 'Ocurrió un error al guardar la empresa, intenta más tarde.';


// file / previewUrl son estado local del formulario: el servicio convierte file a base64.
function buildImage(url = null) {
  return { url: url || null, file: null, previewUrl: null };
}

function defaultCompany() {
  return {
    name: '',
    businessName: '',
    description: '',
    countryId: '',
    // null, no '': un string vacío se convierte en un Date inválido al
    // llegar al backend y Postgres rechaza el insert (columna nullable).
    startDate: null,
    membershipLimit: null,
    type: '',
    email: '',
    rut: '',
    userObj: 100,
    membershipObj: 100,
    metric: 'percentage',
    useGoal: 10
  };
}

export default {
  name: 'CompanyMaintainer',
  components: { ImagePreviewDialog },
  props: ['company'],
  data() {
    return {
      loading: false,
      saveError: '',
      newCompany: defaultCompany(),
      images: { logo: buildImage(), iconApp: buildImage() },
      previewBoxStyle: PREVIEW_BOX_STYLE,
      previewImgStyle: PREVIEW_IMG_STYLE,
      clickableStyle: { cursor: 'zoom-in' },
      viewer: { open: false, src: null, title: '' },
      // Fuente que no cargó, por campo: un archivo o URL nuevos vuelven a intentarse.
      brokenPreviews: {},
      imageFields: IMAGE_FIELDS,
      formValid: false,
      nameRules: [v => !!(v && v.trim()) || 'Este campo es requerido'],
      imageRules: [
        file => !file || FileUtils.isAllowedImage(file) || 'La imagen debe ser PNG, JPG o SVG',
        file => !file || file.size <= MAX_IMAGE_BYTES || 'La imagen supera 1 MB'
      ],
      countries: [],
      countrySelected: { id: null, name: '', iso: '', currency: '', flagUrlImage: '', flagName: '' }
    };
  },
  mounted() {
    this.resetForm();
    // Cargar países primero, luego parsear la empresa (para que countrySelected se setee correctamente)
    this.getCountries();
  },
  beforeDestroy() {
    this.revokePreviews();
  },
  watch: {
    company() {
      this.resetForm();
    }
  },
  methods: {
    resetForm() {
      this.revokePreviews();
      this.newCompany = defaultCompany();
      this.images = { logo: buildImage(), iconApp: buildImage() };
      this.saveError = '';
      this.$refs.newCompanyForm?.resetValidation();
      if (this.company) this.parseCompany();
      else this.selectCompanyCountry();
    },
    closeDialog() {
      this.$emit('close-dialog');
    },
    parseCompany() {
      const c = this.company;
      this.newCompany = {
        name: c.name,
        businessName: c.businessName || '',
        description: c.description || '',
        active: c.active,
        startDate: c.startDate ? c.startDate.toString().substring(0, 10) : null,
        membershipLimit: c.membershipLimit ?? null,
        type: c.type || '',
        email: c.email || '',
        rut: c.rut || '',
        userObj: c.userObj ?? null,
        membershipObj: c.membershipObj ?? null,
        metric: c.metric || 'numeric',
        useGoal: c.useGoal ?? null
      };
      this.images = { logo: buildImage(c.logoUrl), iconApp: buildImage(c.urlIconApp) };
      this.selectCompanyCountry();
    },
    selectCompanyCountry() {
      const match = this.company && this.countries.find(ct => ct.id === this.company.countryId);
      this.countrySelected = match || this.countries[0] || this.countrySelected;
    },
    currentFileName(url) {
      return FileUtils.fileNameFromUrl(url);
    },
    openViewer(src, title) {
      if (!src) return;
      this.viewer = { open: true, src, title };
    },
    isPreviewBroken(key) {
      const src = this.imagePreviewSrc(key);
      return !!src && this.brokenPreviews[key] === src;
    },
    markPreviewBroken(key) {
      this.$set(this.brokenPreviews, key, this.imagePreviewSrc(key));
    },
    imagePreviewSrc(key) {
      return this.images[key].previewUrl || this.images[key].url;
    },
    revokePreview(key) {
      const image = this.images[key];
      if (!image.previewUrl) return;
      URL.revokeObjectURL(image.previewUrl);
      image.previewUrl = null;
    },
    revokePreviews() {
      IMAGE_FIELDS.forEach(field => this.revokePreview(field.key));
    },
    onImageChange(key) {
      this.revokePreview(key);
      const file = this.images[key].file;
      if (file && FileUtils.isAllowedImage(file)) this.images[key].previewUrl = URL.createObjectURL(file);
    },
    removeImage(key) {
      this.revokePreview(key);
      this.images[key].file = null;
      this.images[key].url = null;
    },
    // Razón social es NOT NULL en BD y el formulario no la pide: se usa el nombre.
    buildPayload() {
      const name = this.newCompany.name.trim();
      const { logo, iconApp } = this.images;
      return {
        ...this.newCompany,
        name,
        businessName: name,
        countryId: this.countrySelected.id,
        logoUrl: logo.url,
        logoFile: logo.file || null,
        urlIconApp: iconApp.url,
        iconAppFile: iconApp.file || null
      };
    },
    errorMessage(res) {
      return SAVE_ERRORS[res.code] || DEFAULT_SAVE_ERROR;
    },
    validate() {
      if (this.$refs.newCompanyForm.validate()) this.save();
    },
    async save() {
      this.loading = true;
      this.saveError = '';
      const payload = this.buildPayload();
      const res =
        this.company === null
          ? await CompaniesService.createCompany(payload)
          : await CompaniesService.updateCompany(this.company.id, payload);
      this.loading = false;
      if (res.status !== 'ok') {
        this.saveError = this.errorMessage(res);
        return;
      }
      this.$refs.newCompanyForm.resetValidation();
      this.closeDialog();
    },
    changeCountry(value) {
      this.countrySelected = this.countries.find(c => c.id === value) || this.countrySelected;
    },
    async getCountries() {
      const res = await CountryService.getAllCountries();
      if (res.status !== 'ok') return;
      this.countries = res.data.map(c => ({ ...c, flagName: `${c.name}-${c.flagUrlImage}` }));
      // La empresa pudo parsearse antes de que llegaran los países.
      this.selectCompanyCountry();
    }
  }
};
</script>

<style scoped>
/* El nombre del archivo actual no debe ensanchar el campo y empujar la vista previa fuera de la columna. */
.image-input {
  flex: 1 1 0;
  min-width: 0;
}
.image-input ::v-deep input,
.image-input ::v-deep .v-file-input__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
